import React from 'react';
import { Card, Typography, Skeleton, Empty, Tooltip, Button } from 'antd';
import { TableOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../../hooks/useResponsive';
import { useAppSelector } from '../../hooks/redux';
import { useGetTablesQuery } from '../../services/tablesApi';
import type { Table, TableStatus } from '../../types/tables';
import { useHubEvent } from '../../hooks/useHubEvent';
import { TABLES_CHANGED_EVENT } from '../../services/realtimeHub';

const { Text } = Typography;

// Misma convención de color que la vista de Mesas (tables.css)
const STATUS_STYLES: Record<TableStatus, { bg: string; color: string; border: string; label: string }> = {
  Available: { bg: 'var(--bg-success-light)', color: 'var(--color-success)', border: 'var(--border-success)', label: 'Disponible' },
  Occupied: { bg: 'var(--bg-error-light)', color: 'var(--color-error)', border: 'var(--border-error)', label: 'Ocupada' },
  PreCheck: { bg: 'var(--bg-warning-light)', color: 'var(--color-warning)', border: 'var(--border-warning)', label: 'Pre-check' },
  Reserved: { bg: 'var(--bg-reserved-light)', color: 'var(--color-reserved)', border: 'var(--border-reserved)', label: 'Reservada' },
  Dirty: { bg: 'var(--bg-tertiary)', color: 'var(--color-neutral-text)', border: 'var(--border-color-strong)', label: 'Sucia' },
};

const STATUS_ORDER: TableStatus[] = ['Available', 'Occupied', 'PreCheck', 'Reserved', 'Dirty'];

const TablesMapCard: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);

  // Tiempo real vía TablesChanged; el polling queda como fallback lento.
  const { data: tables, isLoading, refetch } = useGetTablesQuery(undefined, {
    skip: !currentRestaurant?.id,
    refetchOnMountOrArgChange: true,
    pollingInterval: 60000,
  });
  useHubEvent(TABLES_CHANGED_EVENT, () => refetch(), !!currentRestaurant?.id);

  if (!currentRestaurant?.id) {
    return null;
  }

  const sortedTables = [...(tables ?? [])].sort(
    (a, b) =>
      a.areaName.localeCompare(b.areaName) ||
      a.number.localeCompare(b.number, undefined, { numeric: true })
  );

  const countByStatus = sortedTables.reduce<Partial<Record<TableStatus, number>>>((acc, t) => {
    acc[t.status] = (acc[t.status] ?? 0) + 1;
    return acc;
  }, {});

  const renderTable = (table: Table) => {
    const style = STATUS_STYLES[table.status] ?? STATUS_STYLES.Available;
    return (
      <Tooltip
        key={table.id}
        title={`Mesa ${table.number} · ${table.areaName} · ${table.capacity} personas · ${style.label}`}
      >
        <div
          style={{
            // Ancho flexible: los nombres largos ensanchan el chip hasta un
            // tope y de ahí truncan con ellipsis (el Tooltip trae el completo)
            minWidth: isMobile ? 40 : 46,
            maxWidth: isMobile ? 84 : 96,
            height: isMobile ? 40 : 46,
            padding: '0 8px',
            borderRadius: 9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: table.number.length > 4 ? (isMobile ? 10.5 : 11.5) : (isMobile ? 12 : 13),
            fontWeight: 650,
            backgroundColor: style.bg,
            color: style.color,
            border: `1.5px solid ${style.border}`,
            cursor: 'pointer',
          }}
          onClick={() => navigate('/tables')}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {table.number}
          </span>
        </div>
      </Tooltip>
    );
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TableOutlined style={{ marginRight: 8, color: 'var(--color-reserved-text)' }} />
          <span>Mesas</span>
        </div>
      }
      extra={
        <Button type="link" size="small" onClick={() => navigate('/tables')} style={{ padding: 0 }}>
          Ver mesas <RightOutlined style={{ fontSize: 10 }} />
        </Button>
      }
      style={{ borderRadius: isMobile ? 8 : 12, height: '100%' }}
    >
      {isLoading || !tables ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : sortedTables.length === 0 ? (
        <Empty description="Sin mesas configuradas" style={{ margin: '24px 0' }} />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              maxHeight: 220,
              overflowY: 'auto',
              marginBottom: 14,
            }}
          >
            {sortedTables.map(renderTable)}
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px 14px',
              paddingTop: 10,
              borderTop: '1px solid var(--border-color)',
            }}
          >
            {STATUS_ORDER.filter(
              (status) => (countByStatus[status] ?? 0) > 0 || status === 'Available' || status === 'Occupied'
            ).map((status) => {
              const style = STATUS_STYLES[status];
              return (
                <span key={status} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: 3,
                      backgroundColor: style.color,
                      display: 'inline-block',
                    }}
                  />
                  <Text type="secondary" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                    {style.label} · {countByStatus[status] ?? 0}
                  </Text>
                </span>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
};

export default TablesMapCard;
